import {
  type CreateAdminProfessionDto,
  type UpdateAdminProfessionDto,
  type V2AdminProfessionDto,
  type V2AdminProfessionLookupDto,
  type V2AdminProfessionsPageDto,
  type V2AdminProfessionsSummaryDto,
  v2AdminProfessionsControllerCreate,
  v2AdminProfessionsControllerDetail,
  v2AdminProfessionsControllerList,
  v2AdminProfessionsControllerLookup,
  v2AdminProfessionsControllerRemove,
  v2AdminProfessionsControllerSummary,
  v2AdminProfessionsControllerUpdate
} from '@idc-forms/api-client'
import {
  V2AdminProfessionLookupSchema,
  V2AdminProfessionSchema,
  V2AdminProfessionsPageSchema,
  V2AdminProfessionsSummarySchema
} from '@idc-forms/schemas/api-v2/admin-professions'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * Administración del catálogo de profesiones contra la API v2 (ADR 0019/0020).
 * Consume el SDK generado con el Bearer de la sesión staff v2, pagina por cursor y
 * usa el contrato de consulta rico. Identifica todo por id público (`prf_`) y la
 * autoría por `usr_`, nunca como ObjectId.
 *
 * `v2RequestOptions` es OBLIGATORIO en cada llamada: trae el `querySerializer` con
 * bracket notation. El serializer por defecto del SDK usa `deepObject` de UN solo
 * nivel y explota en runtime ante cualquier filtro anidado del ADR 0020.
 */

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
export type ProfessionsAdminV2Query = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

export type ProfessionsAdminV2Row = V2AdminProfessionsPageDto['data'][number]

function toListQuery(query: ProfessionsAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const professionsAdminV2Service = {
  list: async (
    accessToken: string,
    query: ProfessionsAdminV2Query
  ): Promise<V2AdminProfessionsPageDto> => {
    const result = await v2AdminProfessionsControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminProfessionsPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<ProfessionsAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminProfessionsSummaryDto> => {
    const result = await v2AdminProfessionsControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminProfessionsSummarySchema, result.data)
  },

  /** Selector de los formularios KYC: `prf_`, nombre e `isOther`. */
  lookup: async (accessToken: string): Promise<V2AdminProfessionLookupDto> => {
    const result = await v2AdminProfessionsControllerLookup({
      ...v2RequestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2AdminProfessionLookupSchema, result.data)
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminProfessionDto> => {
    const result = await v2AdminProfessionsControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminProfessionSchema, result.data)
  },

  create: async (
    accessToken: string,
    body: CreateAdminProfessionDto
  ): Promise<V2AdminProfessionDto> => {
    const result = await v2AdminProfessionsControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  update: async (
    accessToken: string,
    id: string,
    body: UpdateAdminProfessionDto
  ): Promise<V2AdminProfessionDto> => {
    const result = await v2AdminProfessionsControllerUpdate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminProfessionsControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  }
}
